// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure logic behind the desktop workspace views: one status order for a
// view that mixes lists (folder, space, Everything, Assigned to me), grouping
// by status / due / list / priority / assignee, sorting, client filters, the
// due-date wording, board columns and the overview's status bars. No app state
// here, so it is unit tested on its own.

import type {
	KonvoTask,
	KonvoTaskListStatus,
	KonvoTaskStatus,
	KonvoTaskUser,
} from '@app/features/konvo_tasks/state/KonvoTasks';

export type WorkspaceGroupBy = 'status' | 'due' | 'list' | 'priority' | 'assignee';
export type WorkspaceSortBy = 'due' | 'priority' | 'name' | 'created' | 'updated';
export type DueBucket = 'overdue' | 'today' | 'week' | 'later' | 'none';

export interface WorkspaceStatus {
	/** Lower-case status name, the key across lists. */
	key: string;
	status: string;
	color: string | null;
	type: string | null;
	closed: boolean;
}

export interface TaskGroup {
	key: string;
	kind: WorkspaceGroupBy;
	/** Status groups (and board columns). */
	status?: WorkspaceStatus;
	due?: DueBucket;
	list?: {id: string; name: string; path: string};
	priority?: number | null;
	/** Assignee groups; null = unassigned. */
	user?: KonvoTaskUser | null;
	closed: boolean;
	tasks: Array<KonvoTask>;
}

const TYPE_RANK: Record<string, number> = {open: 0, unstarted: 0.1, custom: 0.5, active: 0.5, done: 0.95, closed: 1};

export function statusKeyOf(status: Pick<KonvoTaskStatus, 'status'> | null | undefined): string {
	return (status?.status ?? '').trim().toLowerCase();
}

export function isClosedType(type: string | null | undefined): boolean {
	return type === 'closed' || type === 'done';
}

/**
 * One status order for tasks from several lists. Each list contributes its
 * statuses at their relative position (0 = first, 1 = last); a name shared by
 * lists sits at the average. Statuses we only know from tasks fall back to
 * their ClickUp type. Open statuses come first, closed ones last.
 */
export function mergeStatuses(
	lists: ReadonlyArray<ReadonlyArray<KonvoTaskListStatus>>,
	tasks: ReadonlyArray<Pick<KonvoTask, 'status'>> = [],
): Array<WorkspaceStatus> {
	const seen = new Map<string, WorkspaceStatus & {positions: Array<number>; first: number}>();
	let counter = 0;
	const note = (status: KonvoTaskStatus, position: number | null) => {
		const key = statusKeyOf(status);
		if (!key) return;
		let entry = seen.get(key);
		if (!entry) {
			entry = {
				key,
				status: status.status ?? key,
				color: status.color,
				type: status.type,
				closed: isClosedType(status.type),
				positions: [],
				first: counter++,
			};
			seen.set(key, entry);
		}
		if (position != null) entry.positions.push(position);
		if (!entry.color && status.color) entry.color = status.color;
	};
	for (const list of lists) {
		const sorted = [...list].sort((a, b) => a.orderindex - b.orderindex);
		sorted.forEach((status, index) => note(status, sorted.length > 1 ? index / (sorted.length - 1) : 0));
	}
	for (const task of tasks) note(task.status, null);
	const position = (entry: {positions: Array<number>; type: string | null}) =>
		entry.positions.length > 0
			? entry.positions.reduce((sum, value) => sum + value, 0) / entry.positions.length
			: (TYPE_RANK[entry.type ?? 'custom'] ?? 0.5);
	return [...seen.values()]
		.sort(
			(a, b) =>
				Number(a.closed) - Number(b.closed) ||
				Number(b.type === 'open') - Number(a.type === 'open') ||
				position(a) - position(b) ||
				a.first - b.first,
		)
		.map(({key, status, color, type, closed}) => ({key, status, color, type, closed}));
}

/**
 * The List view's status order, as ClickUp's List view shows it: furthest
 * along first (the list's statuses from last to first, so not-started ones
 * sit at the bottom), then closed.
 */
export function clickUpListOrder(statuses: ReadonlyArray<WorkspaceStatus>): Array<WorkspaceStatus> {
	const open = statuses.filter((status) => !status.closed).reverse();
	return [...open, ...statuses.filter((status) => status.closed)];
}

const DAY = 86_400_000;

function startOfDay(ms: number): number {
	const date = new Date(ms);
	return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function dueBucket(task: Pick<KonvoTask, 'due_date'>, now = Date.now()): DueBucket {
	if (task.due_date == null) return 'none';
	const today = startOfDay(now);
	const day = startOfDay(task.due_date);
	if (day < today) return 'overdue';
	if (day < today + DAY) return 'today';
	if (day < today + 7 * DAY) return 'week';
	return 'later';
}

export type DueTone = 'overdue' | 'today' | 'tomorrow' | 'date' | 'none';

/** How a due date reads in a row: red when late (open tasks only), bold for today and tomorrow. */
export function dueTone(task: Pick<KonvoTask, 'due_date' | 'status'>, now = Date.now()): DueTone {
	if (task.due_date == null) return 'none';
	const today = startOfDay(now);
	const day = startOfDay(task.due_date);
	if (day < today) return isClosedType(task.status.type) ? 'date' : 'overdue';
	if (day === today) return 'today';
	if (day === today + DAY) return 'tomorrow';
	return 'date';
}

/** "26 Sep"; the year only when it isn't this year. */
export function shortDate(ms: number, now = Date.now()): string {
	const date = new Date(ms);
	return date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: date.getFullYear() === new Date(now).getFullYear() ? undefined : 'numeric',
	});
}

const PRIORITY_NONE = 5;

function priorityRank(task: Pick<KonvoTask, 'priority'>): number {
	const id = task.priority?.id;
	return id != null && id >= 1 && id <= 4 ? id : PRIORITY_NONE;
}

function byDue(a: KonvoTask, b: KonvoTask): number {
	const ad = a.due_date ?? Number.POSITIVE_INFINITY;
	const bd = b.due_date ?? Number.POSITIVE_INFINITY;
	if (ad !== bd) return ad < bd ? -1 : 1;
	return (b.date_created ?? 0) - (a.date_created ?? 0);
}

export function sortTasks(tasks: ReadonlyArray<KonvoTask>, sortBy: WorkspaceSortBy): Array<KonvoTask> {
	const out = [...tasks];
	switch (sortBy) {
		case 'priority':
			return out.sort((a, b) => priorityRank(a) - priorityRank(b) || byDue(a, b));
		case 'name':
			return out.sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}) || byDue(a, b));
		case 'created':
			return out.sort((a, b) => (b.date_created ?? 0) - (a.date_created ?? 0));
		case 'updated':
			return out.sort((a, b) => (b.date_updated ?? 0) - (a.date_updated ?? 0));
		default:
			return out.sort(byDue);
	}
}

export interface TaskFilters {
	query?: string;
	/** Status keys to hide. */
	hiddenStatuses?: ReadonlySet<string>;
	/** Priority ids to hide (0 = no priority). */
	hiddenPriorities?: ReadonlySet<number>;
	/** When non-empty, only tasks assigned to one of these ClickUp user ids ('none' = unassigned). */
	assignees?: ReadonlySet<string>;
}

export function filterTasks(tasks: ReadonlyArray<KonvoTask>, filters: TaskFilters): Array<KonvoTask> {
	const query = (filters.query ?? '').trim().toLowerCase();
	return tasks.filter((task) => {
		if (query) {
			const hay = `${task.name} ${task.custom_id ?? ''} ${task.id ?? ''}`.toLowerCase();
			if (!hay.includes(query)) return false;
		}
		if (filters.hiddenStatuses?.size && filters.hiddenStatuses.has(statusKeyOf(task.status))) return false;
		if (filters.hiddenPriorities?.size) {
			const rank = priorityRank(task);
			if (filters.hiddenPriorities.has(rank === PRIORITY_NONE ? 0 : rank)) return false;
		}
		if (filters.assignees?.size) {
			const ids = task.assignees.map((user) => String(user.id));
			const hit = ids.length === 0 ? filters.assignees.has('none') : ids.some((id) => filters.assignees!.has(id));
			if (!hit) return false;
		}
		return true;
	});
}

const DUE_ORDER: Array<DueBucket> = ['overdue', 'today', 'week', 'later', 'none'];

export interface GroupOptions {
	/** The view's statuses in order (from mergeStatuses); status groups follow it. */
	statuses?: ReadonlyArray<WorkspaceStatus>;
	/** Keep status groups that have no tasks (board columns). */
	includeEmpty?: boolean;
	sortBy?: WorkspaceSortBy;
	now?: number;
}

function listPath(task: KonvoTask): string {
	return [task.space.name, task.folder.name, task.list.name].filter(Boolean).join(' / ');
}

export function groupTasks(
	tasks: ReadonlyArray<KonvoTask>,
	groupBy: WorkspaceGroupBy,
	options: GroupOptions = {},
): Array<TaskGroup> {
	const sortBy = options.sortBy ?? 'due';
	const now = options.now ?? Date.now();
	const groups = new Map<string, TaskGroup>();
	const add = (key: string, make: () => Omit<TaskGroup, 'tasks' | 'key'>, task: KonvoTask) => {
		let group = groups.get(key);
		if (!group) {
			group = {key, ...make(), tasks: []};
			groups.set(key, group);
		}
		group.tasks.push(task);
	};
	let order: Array<string> = [];
	switch (groupBy) {
		case 'status': {
			const ordered = [...(options.statuses ?? [])];
			const known = new Map(ordered.map((status) => [status.key, status]));
			// Statuses only seen on tasks (a list we have no meta for) join before the closed ones.
			const extra = mergeStatuses(
				[],
				tasks.filter((task) => !known.has(statusKeyOf(task.status))),
			);
			for (const status of extra) {
				const at = ordered.findIndex((entry) => entry.closed && !status.closed);
				if (at < 0) ordered.push(status);
				else ordered.splice(at, 0, status);
				known.set(status.key, status);
			}
			order = ordered.map((status) => `status:${status.key}`);
			if (options.includeEmpty) {
				for (const status of ordered) {
					const key = `status:${status.key}`;
					groups.set(key, {key, kind: 'status', status, closed: status.closed, tasks: []});
				}
			}
			for (const task of tasks) {
				const status = known.get(statusKeyOf(task.status))!;
				add(`status:${status.key}`, () => ({kind: 'status', status, closed: status.closed}), task);
			}
			break;
		}
		case 'due':
			order = DUE_ORDER.map((bucket) => `due:${bucket}`);
			for (const task of tasks) {
				const due =
					isClosedType(task.status.type) && task.due_date != null && dueBucket(task, now) === 'overdue'
						? 'later'
						: dueBucket(task, now);
				add(`due:${due}`, () => ({kind: 'due', due, closed: false}), task);
			}
			break;
		case 'list':
			for (const task of tasks) {
				const id = task.list.id ?? '';
				add(
					`list:${id}`,
					() => ({kind: 'list', list: {id, name: task.list.name ?? '', path: listPath(task)}, closed: false}),
					task,
				);
			}
			order = [...groups.values()].sort((a, b) => a.list!.path.localeCompare(b.list!.path)).map((group) => group.key);
			break;
		case 'priority':
			order = [1, 2, 3, 4, 0].map((id) => `priority:${id}`);
			for (const task of tasks) {
				const rank = priorityRank(task);
				const id = rank === PRIORITY_NONE ? 0 : rank;
				add(`priority:${id}`, () => ({kind: 'priority', priority: id || null, closed: false}), task);
			}
			break;
		case 'assignee':
			for (const task of tasks) {
				if (task.assignees.length === 0)
					add('assignee:none', () => ({kind: 'assignee', user: null, closed: false}), task);
				for (const user of task.assignees)
					add(`assignee:${user.id}`, () => ({kind: 'assignee', user, closed: false}), task);
			}
			order = [...groups.values()]
				.sort((a, b) => {
					if (!a.user) return 1;
					if (!b.user) return -1;
					return (a.user.username ?? '').localeCompare(b.user.username ?? '');
				})
				.map((group) => group.key);
			break;
	}
	return order
		.map((key) => groups.get(key))
		.filter((group): group is TaskGroup => group != null && (options.includeEmpty || group.tasks.length > 0))
		.map((group) => ({...group, tasks: sortTasks(group.tasks, sortBy)}));
}

/** The list status a task should move to for a board column named `key` (null when its list lacks it). */
export function statusInList(statuses: ReadonlyArray<KonvoTaskListStatus>, key: string): KonvoTaskListStatus | null {
	return statuses.find((status) => statusKeyOf(status) === key) ?? null;
}

export interface BarSegment {
	key: string;
	status: string;
	color: string | null;
	type: string | null;
	count: number;
	/** Share of the whole bar, 0–1. */
	share: number;
}

/** Status-split progress bar: segments with tasks, in list order, and the closed share. */
export function progressSegments(
	statuses: ReadonlyArray<{status: string | null; color: string | null; type: string | null; count: number}>,
): {segments: Array<BarSegment>; total: number; closedShare: number} {
	const total = statuses.reduce((sum, status) => sum + Math.max(0, status.count), 0);
	const segments = statuses
		.filter((status) => status.count > 0)
		.map((status) => ({
			key: statusKeyOf(status),
			status: status.status ?? '',
			color: status.color,
			type: status.type,
			count: status.count,
			share: total > 0 ? status.count / total : 0,
		}));
	const closed = segments
		.filter((segment) => isClosedType(segment.type))
		.reduce((sum, segment) => sum + segment.count, 0);
	return {segments, total, closedShare: total > 0 ? closed / total : 0};
}

/** Legend for several lists' bars: each status name once, open ones first, closed ones merged last. */
export function legendFor(
	lists: ReadonlyArray<{
		statuses: ReadonlyArray<{status: string | null; color: string | null; type: string | null; count: number}>;
	}>,
	max = 6,
): Array<{key: string; status: string; color: string | null; closed: boolean}> {
	const merged = mergeStatuses(
		lists.map((list) => list.statuses.map((status, index) => ({...status, orderindex: index}))),
	);
	const open = merged.filter((status) => !status.closed);
	const closed = merged.find((status) => status.closed);
	const out = open
		.slice(0, closed ? max - 1 : max)
		.map(({key, status, color}) => ({key, status, color, closed: false}));
	if (closed) out.push({key: closed.key, status: closed.status, color: closed.color, closed: true});
	return out;
}
